import { X, Ship, MapPin, Gauge, Compass, Navigation } from 'lucide-react'
import type { MonitorLiveVessel } from '@/lib/api'

interface Props {
  vessel: MonitorLiveVessel
  onClose: () => void
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="text-gray-600 mt-0.5">{icon}</span>
      <div className="text-[10px]">
        <span className="text-gray-600">{label}: </span>
        <span className="text-gray-400">{value}</span>
      </div>
    </div>
  )
}

export default function VesselDetailCard({ vessel, onClose }: Props) {
  const speedKt = vessel.speed != null ? vessel.speed.toFixed(1) : '—'
  const course = vessel.course != null ? `${Math.round(vessel.course)}°` : '—'

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded overflow-hidden shadow-[0_0_15px_rgba(34,197,94,0.08)]">
      <div className="flex items-start justify-between px-4 py-3 border-b border-green-900/40 bg-gray-900/50">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="text-sm font-semibold text-gray-100 leading-snug">
            {vessel.name || vessel.mmsi}
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-gray-500">
            <Ship className="w-3.5 h-3.5 text-blue-500" />
            <span>MMSI: {vessel.mmsi}</span>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-600 hover:text-gray-300 transition-colors p-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-3 text-xs font-mono">
        <div className="space-y-1.5">
          <InfoRow
            icon={<MapPin className="w-3 h-3" />}
            label="Position"
            value={`${vessel.latitude.toFixed(4)}, ${vessel.longitude.toFixed(4)}`}
          />
          <InfoRow icon={<Gauge className="w-3 h-3" />} label="Speed" value={`${speedKt} kn`} />
          <InfoRow icon={<Compass className="w-3 h-3" />} label="Course" value={course} />
          {vessel.destination && (
            <InfoRow icon={<Navigation className="w-3 h-3" />} label="Destination" value={vessel.destination} />
          )}
          {vessel.status && (
            <InfoRow icon={<Ship className="w-3 h-3" />} label="Status" value={vessel.status} />
          )}
        </div>
      </div>
    </div>
  )
}
